import { DragDropEvent } from "services/dragdrop/DragDropContext";
import { EventsByResourceType, EventsListType, EventType, ResourceGroupType, SchedulerDraggableId, SchedulerDroppableId } from "../SchedulingTypes";
import { convertDayKeyAndTimeSlotToEndDateTimeString, convertDayKeyAndTimeSlotToStartDateTimeString, generateTimeKeyFromDateTimeString } from "./schedulingDateUtils";
import { convertLocalDateTimeToUTC } from "internationalization/timezoneConversions";
import { saveData } from "services/api/saveData";
import { formatDate } from "services/utils/dateTimeUtils";
import { t } from "i18next";

type ResolvedPosition = {
    resourceId: string | number;
    day: string;
    startTime: string;
};

// Resolve the drop target
export const resolveDropTarget = (
    droppable: SchedulerDroppableId,
    fallbackStartTime: string
): ResolvedPosition | null => {

    if (droppable.type === 'timeSlot') {
        return {
            resourceId: droppable.resourceId,
            day: droppable.day,
            startTime: droppable.timeSlot
        };
    }

    if (droppable.type === 'dayList') {
        return {
            resourceId: droppable.resourceId,
            day: droppable.day,
            startTime: fallbackStartTime
        };
    }

    if (droppable.type === 'timeBlock') {
        return {
            resourceId: droppable.resourceId,
            day: droppable.day,
            startTime: droppable.timeBlock
        };
    }

    return null;
};

export const handleActivityDrag = async (
    dragResult: DragDropEvent<SchedulerDraggableId, SchedulerDroppableId>,
    events: EventsListType,
    resourceGroups: ResourceGroupType[],
    userLocale: string,
    userTimezone: string,
    setFloatingAlert: Function,
    setEvents: React.Dispatch<React.SetStateAction<EventsListType>>,
    setEventsByResource: React.Dispatch<React.SetStateAction<EventsByResourceType>>
) => {

    // Unpack the dragresult
    const { source, destination, draggableId } = dragResult;

    // Handle activity event drag
    if (
        source &&
        destination &&
        source.droppableId &&
        destination.droppableId &&
        draggableId.type === 'activity'
    ) {
        // Get the event from the draggable object
        const event = draggableId.event;

        if (event && event.activityId && event.startTime) {

            // Get the from and to position
            const from = resolveDropTarget(source.droppableId, event.startTime);
            const to = resolveDropTarget(destination.droppableId, event.startTime);

            if (from && to) {
                // Determine if the event is dragged to the same position
                const isSamePosition =
                    from.resourceId === to.resourceId &&
                    from.day === to.day &&
                    from.startTime === to.startTime;

                // When dragged to the same position, do nothing
                if (isSamePosition) return;

                // Prevent dragging to unassigned row
                const isUnassigned = to.resourceId === 'unassigned';
                if (isUnassigned) {
                    setFloatingAlert({
                        type: 'warning',
                        message: t('scheduling.scheduling_board.activity_event_dragging_unassigned')
                    });
                    return;
                }

                // Prevent dragging to an equipment row, an activity always needs an employee
                const isEquipmentRow = resourceGroups.some(group => group.resources.some(
                    resource => String(resource.id) === String(to.resourceId) && resource.resource_type === 'equipment'
                ));
                if (isEquipmentRow) {
                    setFloatingAlert({
                        type: 'warning',
                        message: t('scheduling.scheduling_board.activity_event_dragging_equipment')
                    });
                    return;
                }

                await updateEventAndActivityAfterDrag(
                    event,
                    resourceGroups,
                    events,
                    from.resourceId,
                    to.resourceId,
                    to.day,
                    to.startTime,
                    userLocale,
                    userTimezone,
                    setEvents,
                    setEventsByResource,
                    setFloatingAlert
                );
            }
        }
    }
};

// Updates the event and activity after drag
const updateEventAndActivityAfterDrag = async (
    draggableEvent: EventType,
    resourceGroups: ResourceGroupType[],
    events: EventsListType,
    sourceResourceId: string | number,
    destinationResourceId: string | number,
    destinationDay: string,
    destinationStartTime: string,
    userLocale: string,
    userTimezone: string,
    setEvents: React.Dispatch<React.SetStateAction<EventsListType>>,
    setEventsByResource: React.Dispatch<React.SetStateAction<EventsByResourceType>>,
    setFloatingAlert: Function
) => {
    // Get the original event
    const eventKey = draggableEvent.key;
    const originalEvent = events[eventKey];

    // Only allow updating of activity events
    if (draggableEvent.type !== 'activity' || originalEvent.type !== 'activity') return;

    const duration = originalEvent.durationMinutes ?? 60;

    // Delete the event from the former resource
    setEventsByResource(prev => {
        const updated = { ...prev };
        updated[sourceResourceId] =
            updated[sourceResourceId].filter(key => key !== eventKey);
        return updated;
    });

    // Convert the day keys and timeslots to local datetime objects
    const convertedStartDateTime = convertDayKeyAndTimeSlotToStartDateTimeString(destinationDay, destinationStartTime, userLocale, userTimezone);
    const convertedEndDateTime = convertDayKeyAndTimeSlotToEndDateTimeString(destinationDay, destinationStartTime, duration, userLocale, userTimezone);

    // Update the event with the new resouce, day and timeslot
    const updatedEvent = {
        ...originalEvent,
        resourceId: destinationResourceId,
        dayKey: destinationDay,
        timeSlot: destinationStartTime,
        startDateTime: convertedStartDateTime,
        endDateTime: convertedEndDateTime,
        startTime: generateTimeKeyFromDateTimeString(convertedStartDateTime),
        endTime: generateTimeKeyFromDateTimeString(convertedEndDateTime)
    };

    // Update the events state
    setEvents(prev => ({
        ...prev,
        [eventKey]: updatedEvent,
    }));

    // Add the event to the new resource state
    setEventsByResource(prev => {
        const updated = { ...prev };

        // Create a new array for the destination resource if it doesn't exists
        if (!updated[destinationResourceId]) {
            updated[destinationResourceId] = [];
        }

        // Add the event key to the resource
        updated[destinationResourceId].push(eventKey);

        return updated;
    });

    // Convert the local datetimes to utc datetimes
    const utcStart = convertLocalDateTimeToUTC(convertedStartDateTime, userLocale, userTimezone);
    const utcEnd = convertLocalDateTimeToUTC(convertedEndDateTime, userLocale, userTimezone);

    // Configure the submit data for the activity
    const updatedActivity = {
        id: originalEvent.activityId,
        resource: destinationResourceId,
        timezone: userTimezone,
        start_datetime: utcStart,
        end_datetime: utcEnd
    };

    // Save the activity
    const response = await saveData({
        apiUrl: `patch_activity/${originalEvent.activityId}`,
        method: 'patch',
        data: updatedActivity
    });

    // Find the name of the resource based on the destination resource id
    const findResourceName = (resourceId: number | string): string | null => {
        for (const group of resourceGroups) {
            const resource = group.resources.find(res => res.id === resourceId);
            if (resource) {
                return resource.name
            }
        }

        return '';
    };

    // Show floating alert on success
    if (response?.status === 200) {
        setFloatingAlert({
            type: 'success',
            message: t('scheduling.scheduling_board.activity_dragged_to_resource_alert', { resource_name: findResourceName(destinationResourceId), date: formatDate(destinationDay, userLocale, 'writtenMonth'), time: destinationStartTime })
        });
    }
};